/**
 * Grille de prix d'un voyage : des tranches (T1, T2…) et des catégories
 * de voyageur (Salarié, Conjoint, 0 à 2 ans, Supplément single…). Une
 * catégorie suit la tranche (un prix par tranche) ou a un prix unique ; un
 * prix vide veut dire « non proposé ».
 *
 * L'API sert la grille sur l'article (`voyage_grid`) et l'enregistre d'un
 * bloc (`PUT …/voyage_grid`). Ce module convertit entre ce format et l'état
 * du formulaire (prix en €, tranches repérées par une clé stable même
 * avant leur enregistrement), et porte la validation partagée entre
 * l'assistant et la fiche produit.
 */
import type {
  CatalogVoyageGridPayload,
  CatalogVoyageGridResponse,
  CatalogVoyageLineResponse,
} from "../types/catalog.api.types";
import type {
  VoyageCategory,
  VoyageGrid,
  VoyageLine,
  VoyageTier,
} from "../types/yeatiLoc.types";

export const VOYAGE_CATEGORY_ID = "cat_voyage";

export const isVoyageCategory = (categoryId: string | null | undefined): boolean =>
  categoryId === VOYAGE_CATEGORY_ID;

// Préfixe des clés de brouillon : l'API les reçoit telles quelles comme
// `key` d'une tranche nouvelle, et `prices[].tier` y fait référence.
const NEW_KEY_PREFIX = "new-";

const newKey = () => `${NEW_KEY_PREFIX}${crypto.randomUUID()}`;

const toCents = (euros: number) => Math.round(euros * 100);

const isPrice = (value: number | null | undefined): value is number =>
  typeof value === "number" && Number.isFinite(value);

export const makeVoyageTier = (label: string): VoyageTier => ({ key: newKey(), label });

export const makeVoyageCategory = (patch: Partial<VoyageCategory> = {}): VoyageCategory => ({
  key: newKey(),
  label: "",
  follows_tier: false,
  takes_place: true,
  supplement: false,
  age_min: null,
  age_max: null,
  tier_prices: {},
  single_price: null,
  ...patch,
});

/** Grille proposée à la création : trois tranches, le salarié et son
 * conjoint au prix de leur tranche, les enfants et l'accompagnateur à prix
 * unique, un supplément single. Les prix restent à saisir. */
export const buildDefaultVoyageGrid = (): VoyageGrid => ({
  tier_attribute_id: null,
  tiers: ["T1", "T2", "T3"].map(makeVoyageTier),
  categories: [
    makeVoyageCategory({ label: "Salarié", follows_tier: true }),
    makeVoyageCategory({ label: "Conjoint", follows_tier: true }),
    makeVoyageCategory({ label: "Accompagnateur" }),
    makeVoyageCategory({ label: "0 à 2 ans", age_min: 0, age_max: 1 }),
    makeVoyageCategory({ label: "2 à 5 ans", age_min: 2, age_max: 5 }),
    makeVoyageCategory({ label: "6 à 11 ans", age_min: 6, age_max: 11 }),
    makeVoyageCategory({ label: "Supplément single", supplement: true, takes_place: false }),
  ],
});

/** Prix d'une catégorie pour une tranche (clé de tranche), en €. */
export const voyageCategoryPrice = (category: VoyageCategory, tierKey: string | null): number | null => {
  if (!category.follows_tier) return category.single_price;
  if (!tierKey) return null;
  return category.tier_prices[tierKey] ?? null;
};

/** Bascule « suit la tranche » : on reporte le prix existant pour que
 * l'élu ne ressaisisse pas tout (prix unique recopié dans chaque tranche
 * vide, ou première tranche renseignée devenue prix unique). */
export const setVoyageCategoryFollowsTier = (
  category: VoyageCategory,
  followsTier: boolean,
  tiers: VoyageTier[],
): VoyageCategory => {
  if (followsTier) {
    const hasTierPrice = tiers.some((t) => isPrice(category.tier_prices[t.key]));
    return {
      ...category,
      follows_tier: true,
      tier_prices: hasTierPrice
        ? category.tier_prices
        : Object.fromEntries(tiers.map((t) => [t.key, category.single_price])),
    };
  }
  const firstTierPrice = tiers.map((t) => category.tier_prices[t.key]).find(isPrice) ?? null;
  return {
    ...category,
    follows_tier: false,
    single_price: isPrice(category.single_price) ? category.single_price : firstTierPrice,
  };
};

/** Retire une tranche et les prix qui s'y rattachaient. */
export const removeVoyageTier = (grid: VoyageGrid, tierKey: string): VoyageGrid => ({
  // Le reste de la grille (attribut de tranche…) est conservé tel quel.
  ...grid,
  tiers: grid.tiers.filter((t) => t.key !== tierKey),
  categories: grid.categories.map((c) => {
    if (!(tierKey in c.tier_prices)) return c;
    const { [tierKey]: _removed, ...rest } = c.tier_prices;
    return { ...c, tier_prices: rest };
  }),
});

const categoryPrices = (grid: VoyageGrid, category: VoyageCategory): number[] =>
  (category.follows_tier ? grid.tiers.map((t) => category.tier_prices[t.key]) : [category.single_price])
    .filter(isPrice);

const minOf = (prices: number[]): number | null => (prices.length > 0 ? Math.min(...prices) : null);

/** Une catégorie (ou un supplément) sans aucun prix n'est pas proposée :
 * en lecture, elle ne fait pas de ligne et se résume dans « Non proposées ». */
export const voyageCategoryHasPrice = (grid: VoyageGrid, category: VoyageCategory): boolean =>
  categoryPrices(grid, category).length > 0;

export const splitOfferedVoyageCategories = (grid: VoyageGrid) => ({
  offered: grid.categories.filter((c) => voyageCategoryHasPrice(grid, c)),
  notOffered: grid.categories.filter((c) => !voyageCategoryHasPrice(grid, c)),
});

/** Plus petit prix d'un participant (hors suppléments), en €, toutes
 * catégories confondues. Sert à savoir si la grille porte un prix. */
export const voyageMinPrice = (grid: VoyageGrid | null | undefined): number | null => {
  if (!grid) return null;
  return minOf(grid.categories.filter((c) => !c.supplement).flatMap((c) => categoryPrices(grid, c)));
};

/** Prix d'appel, même règle que benef et l'app : le plus petit prix des
 * catégories de voyageur sans borne d'âge (adultes), sinon le plus petit
 * prix de toute catégorie de voyageur. Évite d'afficher le tarif bébé. */
export const voyageFromPrice = (grid: VoyageGrid | null | undefined): number | null => {
  if (!grid) return null;
  const travellers = grid.categories.filter((c) => !c.supplement);
  const withoutAge = travellers.filter((c) => (c.age_min ?? null) === null && (c.age_max ?? null) === null);
  return minOf(withoutAge.flatMap((c) => categoryPrices(grid, c)))
    ?? minOf(travellers.flatMap((c) => categoryPrices(grid, c)));
};

const EUR_FORMATTER = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

/** Prix affiché dans le catalogue pour un voyage. La liste des articles ne
 * sert pas forcément la grille : on reste alors générique. */
export const voyagePriceLabel = (grid: VoyageGrid | null | undefined): string => {
  if (!grid) return "Selon la grille de prix";
  const min = voyageFromPrice(grid);
  return min === null ? "Prix à renseigner" : `À partir de ${EUR_FORMATTER.format(min)}`;
};

/**
 * Premier défaut bloquant de la grille, ou `null`. Partagé entre l'étape
 * Tarification de l'assistant et la fiche produit.
 */
export const voyageGridError = (grid: VoyageGrid | null | undefined): string | null => {
  if (!grid) return "Configurez la grille de prix du voyage";
  if (grid.tiers.length === 0) return "Ajoutez au moins une tranche";
  if (grid.tiers.some((t) => !t.label.trim())) return "Donnez un nom à chaque tranche";
  if (grid.tier_attribute_id) {
    const valueIds = grid.tiers.map((t) => t.custom_attribute_value_id ?? null);
    if (valueIds.some((id) => id === null)) return "Rattachez chaque tranche à une valeur de l'attribut";
    if (new Set(valueIds).size !== valueIds.length) return "Deux tranches sont rattachées à la même valeur de l'attribut";
  }
  if (grid.categories.some((c) => !c.label.trim())) return "Donnez un nom à chaque catégorie";
  if (grid.categories.some((c) => isPrice(c.age_min) && isPrice(c.age_max) && c.age_min > c.age_max)) {
    return "L'âge minimum d'une catégorie dépasse son âge maximum";
  }
  const allPrices = grid.categories.flatMap((c) => [c.single_price, ...grid.tiers.map((t) => c.tier_prices[t.key])]);
  if (allPrices.some((p) => isPrice(p) && p < 0)) return "Les prix doivent être positifs ou nuls";
  if (voyageMinPrice(grid) === null) return "Renseignez le prix d'au moins une catégorie de voyageur";
  return null;
};

// ---------------------------------------------------------------------------
// Tranche lue dans un attribut personnalisé du salarié
// ---------------------------------------------------------------------------

export type VoyageTierAttributeValue = { id: number, value: string };

const sameLabel = (a: string, b: string) => a.trim().toLocaleLowerCase("fr") === b.trim().toLocaleLowerCase("fr");

/** Grille encore à ses tranches par défaut : T1, T2… jamais enregistrées,
 * sans aucun prix de tranche saisi. On peut alors les remplacer sans rien
 * perdre. */
export const hasDefaultVoyageTiers = (grid: VoyageGrid): boolean =>
  grid.tiers.every((t, i) => !t.id && t.label === `T${i + 1}`)
  && grid.categories.every((c) => grid.tiers.every((t) => !isPrice(c.tier_prices[t.key])));

/**
 * À la sélection de l'attribut : une tranche par valeur (libellé = valeur)
 * si la grille n'a que ses tranches par défaut ; sinon, chaque tranche non
 * rattachée l'est à la valeur de même libellé, quand elle est libre.
 */
export const applyVoyageTierAttributeValues = (
  grid: VoyageGrid,
  values: VoyageTierAttributeValue[],
): VoyageGrid => {
  if (values.length === 0) return grid;
  if (hasDefaultVoyageTiers(grid)) {
    const tiers = values.map((v) => ({ ...makeVoyageTier(v.value), custom_attribute_value_id: v.id }));
    return { ...grid, tiers, categories: grid.categories.map((c) => ({ ...c, tier_prices: {} })) };
  }
  const used = new Set(grid.tiers.map((t) => t.custom_attribute_value_id).filter((id) => id !== null && id !== undefined));
  const tiers = grid.tiers.map((t) => {
    if (t.custom_attribute_value_id) return t;
    const match = values.find((v) => !used.has(v.id) && sameLabel(v.value, t.label));
    if (!match) return t;
    used.add(match.id);
    return { ...t, custom_attribute_value_id: match.id };
  });
  return { ...grid, tiers };
};

/** Ajoute une tranche pour chaque valeur de l'attribut encore sans tranche. */
export const addVoyageTiersForUnlinkedValues = (
  grid: VoyageGrid,
  values: VoyageTierAttributeValue[],
): VoyageGrid => {
  const used = new Set(grid.tiers.map((t) => t.custom_attribute_value_id));
  const added = values
    .filter((v) => !used.has(v.id))
    .map((v) => ({ ...makeVoyageTier(v.value), custom_attribute_value_id: v.id }));
  return added.length > 0 ? { ...grid, tiers: [...grid.tiers, ...added] } : grid;
};

// ---------------------------------------------------------------------------
// API → formulaire
// ---------------------------------------------------------------------------

export const adaptVoyageGridResponse = (grid: CatalogVoyageGridResponse): VoyageGrid => {
  const tiers = [...grid.tiers]
    .sort((a, b) => a.position - b.position)
    .map((t) => ({
      key: t.id, id: t.id, label: t.label, custom_attribute_value_id: t.custom_attribute_value_id ?? null,
    }));
  const tierIds = new Set(tiers.map((t) => t.key));

  const categories = [...grid.categories]
    .sort((a, b) => a.position - b.position)
    .map((c) => {
      const tierPrices: Record<string, number | null> = {};
      let singlePrice: number | null = null;
      for (const price of c.prices) {
        if (price.tier_id === null) singlePrice = price.amount_cents / 100;
        else if (tierIds.has(price.tier_id)) tierPrices[price.tier_id] = price.amount_cents / 100;
      }
      return {
        key: c.id,
        id: c.id,
        label: c.label,
        follows_tier: c.follows_tier,
        takes_place: c.takes_place,
        supplement: c.supplement,
        age_min: c.age_min,
        age_max: c.age_max,
        tier_prices: c.follows_tier ? tierPrices : {},
        single_price: c.follows_tier ? null : singlePrice,
      };
    });

  return {
    tier_attribute_id: grid.tier_attribute?.id ?? null,
    tiers,
    categories,
    // Lus seulement : la fiche lue pour un salarié (réservation par l'élu).
    // Absents de la fiche ordinaire, ils ne changent pas la grille éditée.
    ...(grid.tier_attribute ? { tier_attribute_name: grid.tier_attribute.name } : {}),
    ...(grid.employee_price_tier_id !== undefined ? { employee_price_tier_id: grid.employee_price_tier_id } : {}),
    ...(grid.employee_tier_value !== undefined ? { employee_tier_value: grid.employee_tier_value } : {}),
    ...(grid.employee_tier_editable !== undefined ? { employee_tier_editable: grid.employee_tier_editable } : {}),
  };
};

// ---------------------------------------------------------------------------
// Formulaire → API
// ---------------------------------------------------------------------------

/**
 * Payload du `PUT …/voyage_grid`. Les tranches existantes sont désignées
 * par leur `id`, les nouvelles par leur `key` ; une tranche ou une
 * catégorie absente est supprimée côté API. Seuls les prix de la forme
 * retenue partent (par tranche ou unique), les cases vides sont omises.
 */
export const buildVoyageGridPayload = (grid: VoyageGrid): CatalogVoyageGridPayload => {
  const tierRef = (tier: VoyageTier) => tier.id ?? tier.key;
  // Sans attribut, aucune tranche ne reste rattachée à une valeur.
  const attributeId = grid.tier_attribute_id ?? null;
  const valueOf = (tier: VoyageTier) => (attributeId ? tier.custom_attribute_value_id ?? null : null);

  return {
    voyage_grid: {
      tier_custom_attribute_id: attributeId,
      tiers: grid.tiers.map((t) => (t.id
        ? { id: t.id, label: t.label.trim(), custom_attribute_value_id: valueOf(t) }
        : { key: t.key, label: t.label.trim(), custom_attribute_value_id: valueOf(t) })),
      categories: grid.categories.map((c) => {
        const followsTier = c.follows_tier && !c.supplement;
        const prices = followsTier
          ? grid.tiers.flatMap((t) => {
            const price = c.tier_prices[t.key];
            return isPrice(price) ? [{ tier: tierRef(t), amount_cents: toCents(price) }] : [];
          })
          : (isPrice(c.single_price) ? [{ tier: null, amount_cents: toCents(c.single_price) }] : []);
        return {
          ...(c.id ? { id: c.id } : {}),
          label: c.label.trim(),
          follows_tier: followsTier,
          takes_place: c.supplement ? false : c.takes_place,
          supplement: c.supplement,
          age_min: c.age_min,
          age_max: c.age_max,
          prices,
        };
      }),
    },
  };
};

// ---------------------------------------------------------------------------
// Lignes d'une demande
// ---------------------------------------------------------------------------

export const adaptVoyageLines = (lines: CatalogVoyageLineResponse[] | undefined): VoyageLine[] =>
  (lines ?? []).map((l) => ({
    id: l.id,
    label: l.label,
    category_id: l.catalog_tariff_category_id,
    category_label: l.category_label,
    supplement: l.supplement,
    takes_place: l.takes_place,
    quantity: l.quantity,
    unit_price: l.unit_price_cents / 100,
    amount: l.amount_cents / 100,
    participant: l.participant
      ? {
        id: l.participant.id,
        kind: l.participant.kind,
        beneficiary_id: l.participant.beneficiary_id ?? null,
        first_name: l.participant.first_name,
        last_name: l.participant.last_name,
        birth_date: l.participant.birth_date,
        relationship: l.participant.relationship,
        age_at_departure: l.participant.age_at_departure,
      }
      : null,
  }));

/** Places consommées sur le départ : lignes dont la catégorie en prend une. */
export const voyagePlacesCount = (lines: VoyageLine[]): number =>
  lines.reduce((sum, l) => sum + (l.takes_place ? l.quantity : 0), 0);
